import React, { useState, useEffect } from 'react';
import { brokerService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Building2, 
  FileText, 
  Plus,
  AlertTriangle,
  RefreshCw,
  Check,
  X,
  Clock
} from 'lucide-react';

const BrokerPortal = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [activeTab, setActiveTab] = useState('accounts');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    mc_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    payment_terms: 30
  });

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.broker_portal_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadBrokerData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadBrokerData = async () => {
    try {
      const [brokersData, tendersData] = await Promise.all([
        brokerService.getAccounts().catch(() => ({ brokers: [] })),
        brokerService.getTenders('pending').catch(() => ({ tenders: [] }))
      ]);
      
      setBrokers(brokersData.brokers || []);
      setTenders(tendersData.tenders || []);
    } catch (err) {
      console.error('Failed to load broker data:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddBroker = async (e) => {
    e.preventDefault();
    try {
      await brokerService.createAccount(formData);
      alert('Broker account created!');
      setShowAddModal(false);
      setFormData({
        company_name: '',
        mc_number: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        payment_terms: 30
      });
      loadBrokerData();
    } catch (err) {
      alert(err.response?.data?.detail?.message || 'Failed to create broker');
    }
  };

  const handleAcceptTender = async (tenderId) => {
    if (!window.confirm('Accept this load tender?')) return;
    try {
      await brokerService.acceptTender(tenderId);
      alert('Tender accepted! Load created.');
      loadBrokerData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to accept tender');
    }
  };

  const handleRejectTender = async (tenderId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await brokerService.rejectTender(tenderId, reason);
      alert('Tender rejected.');
      loadBrokerData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject tender');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    The Broker Portal is currently disabled. Contact your administrator to enable this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Broker Portal</h1>
            <p className="text-gray-600 mt-1">Manage broker accounts and load tenders</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Broker
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{brokers.length}</div>
                  <div className="text-sm text-gray-500">Total Brokers</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold">{tenders.length}</div>
                  <div className="text-sm text-gray-500">Pending Tenders</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Check className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{brokers.filter(b => b.is_active).length}</div>
                  <div className="text-sm text-gray-500">Active</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">--</div>
                  <div className="text-sm text-gray-500">Avg Pay Terms</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-6">
          {['accounts', 'tenders'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'accounts' ? 'Broker Accounts' : 'Load Tenders'}
            </button>
          ))}
        </div>

        {activeTab === 'accounts' && (
          <Card>
            <CardHeader>
              <CardTitle>Broker Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              {brokers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No broker accounts yet. Add your first broker.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Company</th>
                        <th className="text-left py-3 px-2">MC #</th>
                        <th className="text-left py-3 px-2">Contact</th>
                        <th className="text-left py-3 px-2">Terms</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brokers.map(broker => (
                        <tr key={broker.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{broker.company_name}</td>
                          <td className="py-3 px-2">{broker.mc_number}</td>
                          <td className="py-3 px-2">
                            <div>{broker.contact_name}</div>
                            <div className="text-xs text-gray-500">{broker.contact_email}</div>
                          </td>
                          <td className="py-3 px-2">{broker.payment_terms} days</td>
                          <td className="py-3 px-2">
                            <Badge className={broker.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {broker.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <Button variant="ghost" size="sm">View</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'tenders' && (
          <Card>
            <CardHeader>
              <CardTitle>Pending Load Tenders</CardTitle>
            </CardHeader>
            <CardContent>
              {tenders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pending load tenders.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tenders.map(tender => (
                    <div key={tender.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium">{tender.broker?.company_name || 'Unknown Broker'}</div>
                          <div className="text-sm text-gray-500">Ref: {tender.broker_reference}</div>
                        </div>
                        <Badge className="bg-orange-100 text-orange-800">Pending</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                        <div>
                          <div className="text-gray-500">Origin</div>
                          <div>{tender.origin_city}, {tender.origin_state}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Destination</div>
                          <div>{tender.destination_city}, {tender.destination_state}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Rate</div>
                          <div className="font-medium text-green-600">${tender.rate?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Miles</div>
                          <div>{tender.miles}</div>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => handleRejectTender(tender.id)}>
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => handleAcceptTender(tender.id)}>
                          <Check className="w-4 h-4 mr-1" /> Accept
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Broker Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Add Broker Account</h3>
              <form onSubmit={handleAddBroker} className="space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input name="company_name" value={formData.company_name} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>MC Number *</Label>
                  <Input name="mc_number" value={formData.mc_number} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Contact Name *</Label>
                  <Input name="contact_name" value={formData.contact_name} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Contact Email *</Label>
                  <Input type="email" name="contact_email" value={formData.contact_email} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Contact Phone *</Label>
                  <Input name="contact_phone" value={formData.contact_phone} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Payment Terms (days)</Label>
                  <Input type="number" name="payment_terms" value={formData.payment_terms} onChange={handleInputChange} />
                </div>
                <div className="flex gap-3 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                  <Button type="submit">Create</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BrokerPortal;
